import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { propertyManagementIncomeAPI, propertyAPI } from '../../services/api';
import { saveAs } from 'file-saver';
import SuccessModal from '../Common/SuccessModal';
import ConfirmModal from '../Common/ConfirmModal';
import ErrorModal from '../Common/ErrorModal';
import './PropertyManagementIncomeList.css';

const PropertyManagementIncomeList = () => {
  const { t } = useTranslation();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [page, setPage] = useState(1);
  const [pageSize] = useState(10);
  const [totalPages, setTotalPages] = useState(1);
  const [selectedIds, setSelectedIds] = useState([]);
  const [showErrorModal, setShowErrorModal] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [showExportMenu, setShowExportMenu] = useState(false);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [confirmAction, setConfirmAction] = useState(null);
  const [confirmData, setConfirmData] = useState(null);
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [summary, setSummary] = useState(null);
  const [loadingSummary, setLoadingSummary] = useState(false);

  
  useEffect(() => {
    loadItems();
    if (dateFrom || dateTo) {
      loadSummary();
    } else {
      setSummary(null);
    }
  }, [page, dateFrom, dateTo]);

  const loadItems = async () => {
    try {
      setLoading(true);
      const response = await propertyManagementIncomeAPI.getAll(searchTerm, page, pageSize, dateFrom || null, dateTo || null);
      setItems(response.data.items || []);
      setTotalPages(response.data.totalPages || 1);
    } catch (err) {
      setErrorMessage(t('propertyManagementIncome.list.messages.loadError'));
      setShowErrorModal(true);
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const loadSummary = async () => {
    if (!dateFrom && !dateTo) {
      setSummary(null);
      return;
    }
    try {
      setLoadingSummary(true);
      const response = await propertyManagementIncomeAPI.getSummary(dateFrom || null, dateTo || null);
      setSummary(response.data);
    } catch (err) {
      console.error('Error loading summary:', err);
      setSummary(null);
    } finally {
      setLoadingSummary(false);
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    setPage(1);
    loadItems();
    loadSummary();
  };

  const handleClearDateFilter = () => {
    setDateFrom('');
    setDateTo('');
    setPage(1);
  };

  const handleDelete = (id) => {
    setConfirmAction(() => async () => {
      try {
        await propertyManagementIncomeAPI.delete(id);
        setSuccessMessage(t('propertyManagementIncome.list.messages.deleteSuccess'));
        setShowSuccessModal(true);
        loadItems();
      } catch (err) {
        setErrorMessage(t('propertyManagementIncome.list.messages.deleteError'));
        setShowErrorModal(true);
      }
    });
    setConfirmData({
      title: t('common.confirm'),
      message: t('propertyManagementIncome.list.messages.deleteConfirm'),
      confirmText: t('common.delete'),
      cancelText: t('common.cancel')
    });
    setShowConfirmModal(true);
  };

  const handleBulkDelete = () => {
    if (selectedIds.length === 0) {
      setErrorMessage(t('propertyManagementIncome.list.messages.selectToDelete'));
      setShowErrorModal(true);
      return;
    }

    setConfirmAction(() => async () => {
      try {
        const response = await propertyManagementIncomeAPI.bulkDelete(selectedIds);
        setSuccessMessage(response.data.message || t('propertyManagementIncome.list.messages.bulkDeleteSuccess', { count: selectedIds.length }));
        setShowSuccessModal(true);
        setSelectedIds([]);
        loadItems();
      } catch (err) {
        setErrorMessage(err.response?.data?.message || t('propertyManagementIncome.list.messages.bulkDeleteError'));
        setShowErrorModal(true);
      }
    });
    setConfirmData({
      title: t('common.confirm'),
      message: t('propertyManagementIncome.list.messages.bulkDeleteConfirm', { count: selectedIds.length }),
      confirmText: t('common.delete'),
      cancelText: t('common.cancel')
    });
    setShowConfirmModal(true);
  };

  const handleConfirm = async () => {
    setShowConfirmModal(false);
    if (confirmAction) {
      await confirmAction();
    }
    setConfirmAction(null);
    setConfirmData(null);
  };

  const handleCancel = () => {
    setShowConfirmModal(false);
    setConfirmAction(null);
    setConfirmData(null);
  };

  const handleSelectAll = (e) => {
    if (e.target.checked) {
      setSelectedIds(items.map(item => item.id));
    } else {
      setSelectedIds([]);
    }
  };

  const handleSelectOne = (id) => {
    if (selectedIds.includes(id)) {
      setSelectedIds(selectedIds.filter(i => i !== id));
    } else {
      setSelectedIds([...selectedIds, id]);
    }
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (showExportMenu && !event.target.closest('.export-menu-container')) {
        setShowExportMenu(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showExportMenu]);

  const handleExport = async (format) => {
    try {
      setShowExportMenu(false);
      let response;
      let filename;
      let contentType;

      switch (format) {
        case 'excel':
          response = await propertyManagementIncomeAPI.exportExcel();
          filename = `PropertyManagementIncome_${new Date().toISOString().slice(0, 10)}.xlsx`;
          contentType = 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';
          break;
        case 'csv':
          response = await propertyManagementIncomeAPI.exportCsv();
          filename = `PropertyManagementIncome_${new Date().toISOString().slice(0, 10)}.csv`;
          contentType = 'text/csv;charset=utf-8';
          break;
        case 'pdf':
          response = await propertyManagementIncomeAPI.exportPdf();
          filename = `PropertyManagementIncome_${new Date().toISOString().slice(0, 10)}.pdf`;
          contentType = 'application/pdf';
          break;
        default:
          return;
      }

      const blob = new Blob([response.data], { type: contentType });
      saveAs(blob, filename);
      setSuccessMessage(t('propertyManagementIncome.list.messages.exportSuccess', { format: format.toUpperCase() }));
      setShowSuccessModal(true);
    } catch (err) {
      console.error('Export error:', err);
      setErrorMessage(t('propertyManagementIncome.list.messages.exportError', { format: format.toUpperCase() }));
      setShowErrorModal(true);
    }
  };

  if (loading) {
    return (
      <div className="page-container">
        <div className="text-center py-5">
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">{t('common.loading')}</span>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="page-container">
      <div className="list-view-card">
        <div className="page-actions">
          <div className="page-actions-left">
            <Link to="/property-management-income/create" className="btn-add">
              + {t('propertyManagementIncome.list.addNew')}
            </Link>
            <div className="filters-row">
              <div className="search-container">
                <input
                  type="text"
                  className="search-input"
                  placeholder={t('propertyManagementIncome.list.searchPlaceholder')}
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  onKeyPress={(e) => e.key === 'Enter' && handleSearch(e)}
                />
                <button className="search-icon" onClick={handleSearch}>
                  <i className="fas fa-search"></i>
                </button>
              </div>
              <div className="date-filter-container">
                <div className="date-filter-group">
                  <label className="date-filter-label">
                    <i className="fas fa-calendar-alt"></i> {t('propertyManagementIncome.list.dateFrom')}
                  </label>
                  <input
                    type="date"
                    className="date-filter-input"
                    value={dateFrom}
                    onChange={(e) => setDateFrom(e.target.value)}
                  />
                </div>
                <div className="date-filter-group">
                  <label className="date-filter-label">
                    <i className="fas fa-calendar-alt"></i> {t('propertyManagementIncome.list.dateTo')}
                  </label>
                  <input
                    type="date"
                    className="date-filter-input"
                    value={dateTo}
                    onChange={(e) => setDateTo(e.target.value)}
                  />
                </div>
                {(dateFrom || dateTo) && (
                  <button className="btn-clear-filter" onClick={handleClearDateFilter} title={t('common.clear')}>
                    <i className="fas fa-times"></i>
                  </button>
                )}
              </div>
            </div>
          </div>
          <div className="action-buttons">
            <div className="export-menu-container">
              <button className="action-btn action-btn-export" title={t('common.export')} onClick={() => setShowExportMenu(!showExportMenu)}>
                <i className="fas fa-file-export"></i>
              </button>
              {showExportMenu && (
                <div className="export-menu">
                  <button onClick={() => handleExport('excel')}>
                    <i className="fas fa-file-excel"></i> {t('common.exportExcel')}
                  </button>
                  <button onClick={() => handleExport('csv')}>
                    <i className="fas fa-file-csv"></i> {t('common.exportCsv')}
                  </button>
                  <button onClick={() => handleExport('pdf')}>
                    <i className="fas fa-file-pdf"></i> {t('common.exportPdf')}
                  </button>
                </div>
              )}
            </div>
            <button className="action-btn action-btn-delete" title={t('propertyManagementIncome.list.bulkDelete')} onClick={handleBulkDelete} disabled={selectedIds.length === 0}>
              <i className="fas fa-trash"></i>
            </button>
          </div>
        </div>

        <ErrorModal
          show={showErrorModal}
          message={errorMessage}
          onClose={() => setShowErrorModal(false)}
        />

        <SuccessModal
          show={showSuccessModal}
          message={successMessage}
          onClose={() => setShowSuccessModal(false)}
        />

        <ConfirmModal
          show={showConfirmModal}
          title={confirmData?.title}
          message={confirmData?.message}
          confirmText={confirmData?.confirmText}
          cancelText={confirmData?.cancelText}
          onConfirm={handleConfirm}
          onCancel={handleCancel}
        />

        <div className="table-container">
          <table className="data-table">
            <thead>
              <tr>
                <th>
                  <input
                    type="checkbox"
                    onChange={handleSelectAll}
                    checked={selectedIds.length === items.length && items.length > 0}
                  />
                </th>
                <th>{t('propertyManagementIncome.list.columns.id')}</th>
                <th>{t('propertyManagementIncome.list.columns.date')}</th>
                <th>{t('propertyManagementIncome.list.columns.propertyName')}</th>
                <th>{t('propertyManagementIncome.list.columns.totalUnitIncome')}</th>
                <th>{t('propertyManagementIncome.list.columns.type')}</th>
                <th>{t('propertyManagementIncome.list.columns.percentageValue')}</th>
                <th>{t('propertyManagementIncome.list.columns.notes')}</th>
                <th>{t('propertyManagementIncome.list.columns.actions')}</th>
              </tr>
            </thead>
            <tbody>
              {items.length === 0 ? (
                <tr>
                  <td colSpan="9" className="text-center py-4">
                    {t('propertyManagementIncome.list.messages.noData')}
                  </td>
                </tr>
              ) : (
                items.map((item) => (
                  <tr key={item.id}>
                    <td>
                      <input
                        type="checkbox"
                        checked={selectedIds.includes(item.id)}
                        onChange={() => handleSelectOne(item.id)}
                      />
                    </td>
                    <td>{item.id}</td>
                    <td>{item.date ? new Date(item.date).toLocaleDateString() : '-'}</td>
                    <td>{item.property?.nameEng || item.property?.nameAr || '-'}</td>
                    <td>{item.totalUnitIncome?.toFixed(2) || '0.00'}</td>
                    <td>
                      <span className={item.isPercentage ? 'status-yes-text' : 'status-no-text'}>
                        {item.isPercentage ? t('propertyManagementIncome.list.type.percentage') : t('propertyManagementIncome.list.type.fixed')}
                      </span>
                    </td>
                    <td>{item.percentageValue ? `${item.percentageValue}%` : '-'}</td>
                    <td>{item.notes || '-'}</td>
                    <td>
                      <div className="action-buttons-cell">
                        <Link
                          to={`/property-management-income/view/${item.id}`}
                          className="btn btn-sm btn-view"
                          title={t('common.view')}
                        >
                          <i className="fas fa-eye"></i>
                        </Link>
                        <Link
                          to={`/property-management-income/edit/${item.id}`}
                          className="btn btn-sm btn-edit"
                          title={t('common.edit')}
                        >
                          <i className="fas fa-edit"></i>
                        </Link>
                        <button
                          className="btn btn-sm btn-delete"
                          onClick={() => handleDelete(item.id)}
                          title={t('common.delete')}
                        >
                          <i className="fas fa-trash"></i>
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {totalPages > 1 && (
          <div className="pagination-container">
            <ul className="pagination">
              <li className={`page-item ${page === 1 ? 'disabled' : ''}`}>
                <button className="page-link" onClick={() => setPage(page - 1)} disabled={page === 1}>
                  {t('common.previous')}
                </button>
              </li>
              {[...Array(totalPages)].map((_, i) => (
                <li key={i + 1} className={`page-item ${page === i + 1 ? 'active' : ''}`}>
                  <button className="page-link" onClick={() => setPage(i + 1)}>
                    {i + 1}
                  </button>
                </li>
              ))}
              <li className={`page-item ${page === totalPages ? 'disabled' : ''}`}>
                <button className="page-link" onClick={() => setPage(page + 1)} disabled={page === totalPages}>
                  {t('common.next')}
                </button>
              </li>
            </ul>
          </div>
        )}

        {/* Summary/Report Container */}
        {(dateFrom || dateTo) && (
          <div className="summary-container">
            <div className="summary-header">
              <h3 className="summary-title">
                <i className="fas fa-chart-bar"></i> {t('propertyManagementIncome.list.summary.title')}
              </h3>
              {summary && (
                <div className="summary-period">
                  {summary.dateFrom && summary.dateTo && (
                    <span>
                      {new Date(summary.dateFrom).toLocaleDateString()} - {new Date(summary.dateTo).toLocaleDateString()}
                    </span>
                  )}
                </div>
              )}
            </div>
            
            {loadingSummary ? (
              <div className="summary-loading">
                <i className="fas fa-spinner fa-spin"></i> {t('common.loading')}
              </div>
            ) : summary ? (
              <div className="summary-content">
                {/* Key Metrics */}
                <div className="summary-metrics">
                  <div className="summary-metric-card">
                    <div className="metric-icon" style={{ backgroundColor: 'rgba(40, 167, 69, 0.1)', color: '#28a745' }}>
                      <i className="fas fa-list"></i>
                    </div>
                    <div className="metric-content">
                      <div className="metric-label">{t('propertyManagementIncome.list.summary.totalRecords')}</div>
                      <div className="metric-value">{summary.totalCount || 0}</div>
                    </div>
                  </div>
                  
                  <div className="summary-metric-card">
                    <div className="metric-icon" style={{ backgroundColor: 'rgba(191, 79, 31, 0.1)', color: '#bf4f1f' }}>
                      <i className="fas fa-dollar-sign"></i>
                    </div>
                    <div className="metric-content">
                      <div className="metric-label">{t('propertyManagementIncome.list.summary.totalIncome')}</div>
                      <div className="metric-value">{summary.totalIncome?.toFixed(2) || '0.00'} SAR</div>
                    </div>
                  </div>
                  
                  <div className="summary-metric-card">
                    <div className="metric-icon" style={{ backgroundColor: 'rgba(0, 123, 255, 0.1)', color: '#007bff' }}>
                      <i className="fas fa-calculator"></i>
                    </div>
                    <div className="metric-content">
                      <div className="metric-label">{t('propertyManagementIncome.list.summary.averageIncome')}</div>
                      <div className="metric-value">{summary.averageIncome?.toFixed(2) || '0.00'} SAR</div>
                    </div>
                  </div>
                  
                  <div className="summary-metric-card">
                    <div className="metric-icon" style={{ backgroundColor: 'rgba(255, 193, 7, 0.1)', color: '#ffc107' }}>
                      <i className="fas fa-percent"></i>
                    </div>
                    <div className="metric-content">
                      <div className="metric-label">{t('propertyManagementIncome.list.summary.percentageType')}</div>
                      <div className="metric-value">{summary.percentageCount || 0}</div>
                    </div>
                  </div>
                  
                  <div className="summary-metric-card">
                    <div className="metric-icon" style={{ backgroundColor: 'rgba(108, 117, 125, 0.1)', color: '#6c757d' }}>
                      <i className="fas fa-hashtag"></i>
                    </div>
                    <div className="metric-content">
                      <div className="metric-label">{t('propertyManagementIncome.list.summary.fixedType')}</div>
                      <div className="metric-value">{summary.fixedCount || 0}</div>
                    </div>
                  </div>
                </div>

                {/* Income Breakdown */}
                <div className="summary-breakdown">
                  <div className="breakdown-card">
                    <h4 className="breakdown-title">
                      <i className="fas fa-chart-pie"></i> {t('propertyManagementIncome.list.summary.incomeBreakdown')}
                    </h4>
                    <div className="breakdown-content">
                      <div className="breakdown-item">
                        <span className="breakdown-label">{t('propertyManagementIncome.list.summary.percentageIncome')}:</span>
                        <span className="breakdown-value">{summary.totalPercentageIncome?.toFixed(2) || '0.00'} SAR</span>
                      </div>
                      <div className="breakdown-item">
                        <span className="breakdown-label">{t('propertyManagementIncome.list.summary.fixedIncome')}:</span>
                        <span className="breakdown-value">{summary.totalFixedIncome?.toFixed(2) || '0.00'} SAR</span>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Top Properties */}
                {summary.byProperty && summary.byProperty.length > 0 && (
                  <div className="summary-section">
                    <h4 className="section-title">
                      <i className="fas fa-building"></i> {t('propertyManagementIncome.list.summary.topProperties')}
                    </h4>
                    <div className="properties-list">
                      {summary.byProperty.map((prop, index) => (
                        <div key={prop.propertyId} className="property-item">
                          <div className="property-rank">#{index + 1}</div>
                          <div className="property-info">
                            <div className="property-name">{prop.propertyName}</div>
                            <div className="property-stats">
                              <span className="property-count">{prop.count} {t('propertyManagementIncome.list.summary.records')}</span>
                            </div>
                          </div>
                          <div className="property-income">
                            {prop.totalIncome?.toFixed(2) || '0.00'} SAR
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Monthly Breakdown */}
                {summary.byMonth && summary.byMonth.length > 0 && (
                  <div className="summary-section">
                    <h4 className="section-title">
                      <i className="fas fa-calendar-alt"></i> {t('propertyManagementIncome.list.summary.monthlyBreakdown')}
                    </h4>
                    <div className="monthly-list">
                      {summary.byMonth.map((month) => (
                        <div key={`${month.year}-${month.month}`} className="month-item">
                          <div className="month-name">{month.monthName}</div>
                          <div className="month-stats">
                            <span className="month-count">{month.count} {t('propertyManagementIncome.list.summary.records')}</span>
                            <span className="month-income">{month.totalIncome?.toFixed(2) || '0.00'} SAR</span>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            ) : (
              <div className="summary-empty">
                <i className="fas fa-info-circle"></i> {t('propertyManagementIncome.list.summary.noData')}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default PropertyManagementIncomeList;


